import React, { useState, useEffect, useRef, forwardRef } from 'react';
import PropTypes from 'prop-types';

import { components } from 'react-select';
import CreatableSelect from 'react-select/creatable';

import { RE } from '~/modules/helpers/regexp';
import { tokenize } from '~/modules/helpers/string';

import Translate from '~/components/Translate/Translate';

const filterDuplicate = tags => {
    let filtered = [];
    if (tags?.length) {
        const values = [];
        const labels = [];
        filtered = tags.filter(item => {
            const { label, value } = item;
            // prettier-ignore
            if (
                label &&
                !labels.includes(label) &&
                !values.includes(value)
            ) {
                labels.push(label);
                values.push(value);
                return true;
            }
        });
    }
    return filtered; // (unique labels and values)
};

// prettier-ignore
const Tags = forwardRef((props, refVal) => {
    const [inputValue, setInputValue] = useState('');
    const [editedTag, setEditedTag] = useState(null);
    const [isTouched, setIsTouched] = useState(false);
    const [isMarked, setIsMarked] = useState(false);

    const [tagsToRender, setTagsToRender] = useState(filterDuplicate(props.value));
    const alreadyUsedAsLabel = str => tagsToRender?.length && -1 !== tagsToRender.findIndex(tag => str === tag?.label);

    useEffect(() => {
        const val = props.value;
        if (val !== refVal) {
            setTagsToRender(filterDuplicate(val));
        }
        refVal = val;
    }, [props.value]);

    const tagsLimit = isNaN(props.maxTags) ? null : parseInt(props.maxTags);
    const tagsLimitIsExceeded = tagsLimit > 0 && tagsToRender?.length > tagsLimit;

    const defaultNames = ['react-tag-container', 'form-control'];
    const [classNames, setClassNames] = useState(
        (
            props.className?.length // <array|string>
                ? (
                    'string' !== typeof props.className
                        ? props.className // should be an array, take as is
                        : tokenize(props.className)
                ).filter(
                name => !defaultNames.includes(name)
                ).concat(
                defaultNames
                )
                : defaultNames
        )
    ).concat([
        `${
            props.allowAddValues
                ? 'no'
                : 'partially'
        }-disabled`
    ]);

    const addFlagClass = (bool, prefixForTrue, prefixForFalse, suffix) => {
        const a = `${prefixForTrue}-${suffix}`;
        const b = `${prefixForFalse}-${suffix}`;
        const update = classNames.filter(str => prefixForTrue !== str && prefixForFalse !== str);
        update.push(bool ? a : b);
        setClassNames(update);
    };

    /*
    todo: check class concat is not incremental and, if so, consider the next replacement
    const [classNames, setClassNames] = useState((
        props.className?.length // <array|string>
            ? (
                'string' !== typeof props.className
                  ? props.className // should be an array, take as is
                  : tokenize(props.className)
              ).filter(
                  name => !defaultNames.includes(name)
              ).concat(
                  defaultNames
              )
            : defaultNames
    ));

    useEffect(() => {
        addFlagClass(props.allowAddValues, 'no', 'partially', 'disabled');
    }, [props.allowAddValues]);

    */

    const updateIsNoClass = (bool, suffix) => {
        addFlagClass(bool, 'is', 'not', suffix);
    };

    useEffect(() => {
        updateIsNoClass(isTouched, 'touched');
    }, [isTouched]);

    useEffect(() => {
        updateIsNoClass(isMarked, 'marked');
    }, [isMarked]);

    useEffect(() => {
        updateIsNoClass(editedTag, 'editing');
    }, [editedTag]);

    const ref = useRef(null);
    const getTagValueContainerNode = () => ref.current?.controlRef?.firstElementChild;

    const handleTagsUpdate = (value = []) => {
        setTagsToRender(value);
        props.onChange?.(value);
    };

    const restoreTagBy = value => {
        const tagsUpdate = [];
        const dismissedTags = [];
        if (tagsToRender?.length) {
            for (const tag of tagsToRender) {
                if (tag) {
                    tagsUpdate.push(tag);
                    if (tag.dismissed) {
                        dismissedTags.push(tag);
                    }
                }
            }
        }
        if (dismissedTags.length) {
            for (const tag of dismissedTags) {
                if (value === tag.value) {
                    tag.dismissed = false;
                    break; // (tag values are unique)
                }
            }
        }
        handleTagsUpdate(tagsUpdate);
    };

    const multiValueNodeUnderEditing = useRef(null);
    const stopEditTag = () => {
        multiValueNodeUnderEditing.current?.classList.remove('editing');
        multiValueNodeUnderEditing.current = null;
        setEditedTag(null);
    };

    useEffect(() => {
        let tagsThatAreDeletableCount = 0;
        let tagsThatAreNotDeletableCount = 0;
        const zeroTagName = 'tags-0';
        const zeroTagNameIndex = classNames.indexOf(zeroTagName);
        const tagsNumerosity = tagsToRender?.length ?? 0;
        if (1 > tagsNumerosity) { // zero tag...
            if (-1 === zeroTagNameIndex) {
                setClassNames(classNames.concat([zeroTagName])); // (affect input margin style)
            }
        } else { // at least one tag...
            if (-1 !== zeroTagNameIndex) {
                setClassNames(classNames.filter(name => zeroTagName !== name));
            }
            for (const tag of tagsToRender) {
                const tagIsDeletable = false !== tag.deletable;
                if (tagIsDeletable) {
                    ++tagsThatAreDeletableCount;
                } else {
                    ++tagsThatAreNotDeletableCount;
                }
            }
        }
        backspaceRemovesValue.current = tagsNumerosity ? tagsThatAreDeletableCount > 0 : true;
        isClearable.current = tagsNumerosity ? tagsThatAreNotDeletableCount < 1 : true;
    }, [tagsToRender]);

    const scrollRestoration = useRef(null);
    const registerScrollPosition = y => {
        const tagValueContainerNode = getTagValueContainerNode();
        return (
            scrollRestoration.current = tagValueContainerNode
                ? {
                      container: tagValueContainerNode,
                      x: tagValueContainerNode.scrollLeft,
                      y: y ?? tagValueContainerNode.scrollTop
                  }
                : null
        );
    };

    useEffect(() => {
        // addFlagClass(props.allowAddValues, 'no', 'partially', 'disabled');
        const node = registerScrollPosition(0).container;
        node.addEventListener('click', () => {
            registerScrollPosition(null);
        });
        node.addEventListener('scroll', () => {
            const restorationObj = scrollRestoration.current;
            scrollRestoration.current = null;
            if (restorationObj) {
                const { container, x, y } = restorationObj;
                // tagValueContainerNode:
                container.scrollLeft = x;
                container.scrollTop = y;
            }
        });
        return () => {
            // clean up:
            scrollRestoration.current = null;
        };
    }, []);

    const backspaceRemovesValue = useRef(null);
    const isClearable = useRef(null);
    return (
        <CreatableSelect
            {...props}
            isMulti
            classNamePrefix="react-tag"
            className={classNames.join('\u0020')}
            placeholder={<Translate message="Type something and press enter..."/>}
            backspaceRemovesValue={
                backspaceRemovesValue.current
            }
            isClearable={
                isClearable.current
            }
            value={
                tagsToRender || []
            }
            components={{
                MultiValueRemove: mvr => {
                    const { data, innerProps } = mvr;
                    if (data) {
                        const { value, dismissed, deletable } = data;
                        if (
                            false !== deletable &&
                            value !== editedTag?.value
                        ) {
                            if (dismissed) {
                                innerProps.className += '\u0020dismissed';
                                innerProps.onClick = () => {
                                    restoreTagBy(value);
                                };
                            }
                            // to be reviewed as it may be removed:
                            else {
                                const originalClickHandler = innerProps.onClick;
                                innerProps.onClick = event => {
                                    originalClickHandler?.(event);
                                };
                            }
                            return (
                                <components.MultiValueRemove {...mvr}>
                                    {
                                        '\u0020' // omit native content, e.g: svg icon...
                                    }
                                </components.MultiValueRemove>
                            );
                        }
                    } // otherwise not deletable or is under editing, then omit 'X' button:
                    return <></>; // (it must be so.)
                },
                MultiValueLabel: mvl => {
                    const {
                        innerProps,
                        selectProps,
                        data: {
                            value,
                            label,
                            dismissed
                        }
                    } = mvl;
                    if (dismissed) {
                        innerProps.className += '\u0020dismissed';
                    } else {
                        if (selectProps.isValueEditable) {
                            if (editedTag) {
                                if (value === editedTag.value) {
                                    const createTag = () => {
                                        const str = editedTag.label;
                                        if (
                                            str &&
                                            str !== label &&
                                            !alreadyUsedAsLabel(str)
                                        ) {
                                            const tagsUpdate = [...tagsToRender];
                                            for (const tag of tagsUpdate) {
                                                if (value === tag.value) {
                                                    /*
                                                    // pair string value to string label ?
                                                    const prevLabel = tag.label;
                                                    if(value === prevLabel){
                                                        tag.value = str;
                                                    }
                                                    */
                                                    tag.label = str;
                                                    break; // (tag values are unique)
                                                }
                                            }
                                            handleTagsUpdate(tagsUpdate);
                                            return true;
                                        } else {
                                            return false;
                                        }
                                    };
                                    const initialValue = editedTag.prev ?? label;
                                    return (
                                        <input
                                            type="text"
                                            autoFocus={true}
                                            className="edit-input"
                                            defaultValue={initialValue}
                                            onChange={
                                                event => {
                                                    editedTag.label = event.target.value?.trim() || editedTag.prev;
                                                }
                                            }
                                            onFocus={
                                                event => {
                                                    const input = event.target;
                                                    if (!input.focusedOnce) {
                                                        input.focusedOnce = true;
                                                        if ((input.offsetWidth + 5) < input.scrollWidth) {
                                                            // has ellipsis, move cursor to the beginning of the field:
                                                            input.value = initialValue.substring(0, 1);
                                                            input.setRangeText(
                                                                initialValue.substring(1),
                                                                1,
                                                                initialValue.length,
                                                                'end'
                                                            );
                                                            input.setSelectionRange(0, 0);
                                                        }
                                                    }
                                                }
                                            }
                                            onBlur={
                                                event => {
                                                    const input = event.target;
                                                    if (
                                                        initialValue !== input.value &&
                                                        createTag()
                                                    ) {
                                                        stopEditTag();
                                                    }
                                                }
                                            }
                                            onKeyDown={
                                                event => {
                                                    event.stopPropagation();
                                                    const input = event.target;
                                                    // noinspection FallThroughInSwitchStatementJS
                                                    switch (event.key) {
                                                        case 'Enter':
                                                            if (
                                                                initialValue !== input.value &&
                                                                !createTag()
                                                            ) {
                                                                event.preventDefault();
                                                            } // don't break and continue to do same things as escape
                                                        case 'Escape':
                                                            stopEditTag();
                                                            break;
                                                    }
                                                }
                                            }
                                        />
                                    );
                                }
                            }
                            innerProps.onClick = event => {
                                event.stopPropagation();
                                event.preventDefault();
                                const labelNode = event.currentTarget;
                                multiValueNodeUnderEditing.current?.classList.remove('editing');
                                const labelNodeParent = multiValueNodeUnderEditing.current = labelNode?.parentElement;
                                labelNodeParent?.classList.add('editing');
                                const tagValueContainerNode = getTagValueContainerNode();
                                if (tagValueContainerNode) {
                                    tagValueContainerNode.addEventListener('click', event => {
                                        if (tagValueContainerNode === event.target) {
                                            stopEditTag();
                                        }
                                    }, {
                                        once: true // ←-
                                    });
                                }
                                //start: edit-tag
                                setEditedTag({
                                    value,
                                    label,
                                    prev: label
                                });
                            };
                        }
                    } // otherwise:
                    return <components.MultiValueLabel {...mvl}/>;
                },
                DropdownIndicator: null
            }}
            menuIsOpen={false}
            onInputChange={
                props.allowAddValues
                    ? value => {
                          if (inputValue !== value) {
                              setInputValue(value);
                          }
                      }
                    : null
            }
            onChange={
                value => {
                    const tagsUpdate = value || [];
                    const removedTags = [];
                    if (tagsUpdate?.length) {
                        for (const tag of tagsToRender) {
                            if (0 > tagsUpdate.indexOf(tag)) {
                                if (tag.dismissed || false === tag.deletable) {
                                    return; // and keep prevs...
                                }
                                removedTags.push(tag);
                            }
                        }
                    }
                    handleTagsUpdate(tagsUpdate);
                }
            }
            onKeyDown={
                event => {
                    if (tagsLimitIsExceeded) {
                        console.warn('tags limit reached.');
                    } else {
                        switch (event.key) {
                            case 'Tab':
                            case 'Enter':
                                const str = inputValue;
                                if (str) {
                                    const alreadyUsed = [];
                                    const uniquesToAdd = str
                                        .split(RE.CommaSplit) // -> splitted (self)
                                        .reduce(
                                            (acc, value, index, splitted) => {
                                                const trimmedValue = value && value.trim();
                                                if (
                                                    trimmedValue &&
                                                    index === splitted.indexOf(value)
                                                ) {
                                                    (
                                                        alreadyUsedAsLabel(value)
                                                            ? alreadyUsed
                                                            : acc
                                                    ).push(
                                                        {
                                                            dismissed: false,
                                                            deletable: true,
                                                            value: trimmedValue,
                                                            label: trimmedValue
                                                        }
                                                    );
                                                }
                                                return acc;
                                            },
                                            []
                                        );
                                    // console.debug({ str, uniques, alreadyUsed });
                                    if (
                                        alreadyUsed.length &&
                                        !uniquesToAdd.length
                                    ) {
                                        setIsMarked(true);
                                        return false; // (break) because entry is one duplicate.
                                    } else {
                                        handleTagsUpdate(
                                            tagsToRender?.length
                                                ? tagsToRender.concat(uniquesToAdd)
                                                : uniquesToAdd
                                        );
                                        setInputValue('');
                                    }
                                }
                                // don't break and continue to do same things as  default
                            default:
                                if (false !== isMarked) {
                                    setIsMarked(false);
                                }
                        }
                    }
                }
            }
            onFocus={
                event => {
                    props.onFocus?.(event);
                }
            }
            onBlur={
                event => {
                    if (!editedTag) {
                        scrollRestoration.current = null;
                        if (true !== isTouched) {
                            setIsTouched(true);
                        }
                        props.onBlur?.(event);
                    }
                }
            }
            inputValue={inputValue}
            ref={ref}
        />
    );
});

Tags.propTypes = {
    value: PropTypes.any,
    props: PropTypes.object,
    maxTags: PropTypes.number,
    className: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    isValueEditable: PropTypes.bool,
    isDisabled: PropTypes.bool,
    onChange: PropTypes.func
};

Tags.defaultProps = {
    isDisabled: false,
    isValueEditable: false,
    allowAddValues: true
};

export default Tags;

/*
# notes:
  ⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓

   github.com/JedWatson/react-select/issues/2181

   ref.current?.select¹?.select²?.controlRef;

   ¹ creatable
   ² select

  ⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓

  react-select.com/props#multivaluecontainer

  { data, innerProps, selectProps, children }

  ⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓⁓

  caniuse.com/once-event-listener

   firefox: 50
   chrome: 55

*/
